<script setup lang="ts">
import { useAuthStore } from '@/store/auth.js'
import 'vue-loading-overlay/dist/css/index.css'

definePageMeta({
  auth: 'guest',
  layout: false,
})

const authStore = useAuthStore()

const route = useRoute()
const showResetOk = computed(() => route.query.msg === 'reset_ok')

const email = ref('')
const password = ref('')
const emailError = ref('')
const passwordError = ref('')

const validateEmail = (email: string) => {
  const emailPattern = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/
  if (!email.match(emailPattern)) {
    emailError.value = 'Neplatný email'
    return false
  }
  emailError.value = ''
  return true
}

const validatePassword = (password: string) => {
  if (password.length < 4) {
    passwordError.value = 'Heslo musí mít alespoň 4 znaky'
    return false
  }
  passwordError.value = ''
  return true
}

const validateForm = () => {
  const isEmailValid = validateEmail(email.value)
  const isPasswordValid = validatePassword(password.value)
  return isEmailValid && isPasswordValid
}

const handleLogin = async () => {
  if (validateForm()) {
    await authStore.login(email.value, password.value)
  }
}
</script>

<template>
  <NuxtLoadingIndicator :throttle="0" color="#751D2A" />
  <div class="grid place-content-center h-screen">
    <div class="content">
      <div
        v-if="$config.public.vercelEnv !== 'production'"
        class="absolute top-0 left-0 w-full bg-red text-white text-center py-1 text-xs"
      >
        {{ $config.public.vercelEnv.toUpperCase() }}
      </div>
      <p
        v-if="showResetOk"
        class="mb-25 text-success text-center bg-white/50 p-4 rounded-xl shadow"
      >
        Nyní se prosím přihlaste s novým heslem
      </p>
      <img
        class="block max-w-full mx-auto w-[130px] mb-40"
        src="/logos/logo-primary.svg"
        alt="Vanguard Prague"
      />
      <form
        class="w-screen max-w-[420px] block mx-auto p-4 bg-white/50 rounded-xl shadow space-y-4"
        @submit.prevent="handleLogin"
      >
        <h3 class="text-center text-primary">Přihlášení</h3>
        <div>
          <label for="email" class="sr-only">Váš e-mail</label>
          <InputIconGroup>
            <InputIcon name="lucide:user" />
            <Input
              v-model="email"
              id="email"
              placeholder="Váš e-mail"
              type="text"
            />
          </InputIconGroup>
          <FormAlert v-if="emailError">{{ emailError }}</FormAlert>
        </div>
        <div>
          <label for="password" class="sr-only">Heslo</label>
          <InputIconGroup>
            <InputIcon name="lucide:lock" />
            <Input
              v-model="password"
              id="password"
              placeholder="Heslo"
              type="password"
            />
          </InputIconGroup>
          <FormAlert v-if="passwordError">{{ passwordError }}</FormAlert>
        </div>

        <Button
          type="submit"
          variant="primary"
          size="lg"
          class="w-full"
          :disabled="authStore.isLoading"
        >
          Přihlásit se
        </Button>

        <p class="text-center">
          <NuxtLink
            to="/reset-password"
            class="text-base no-underline hover:underline"
          >
            Zapomněli jste své heslo?
          </NuxtLink>
        </p>
      </form>

      <div class="text-center mt-40">
        <a
          href="tel:+420773782589"
          class="text-24 font-bold no-underline inline-flex items-center gap-4"
        >
          <Icon name="lucide:phone" class="size-6" />
          <span class="">+420 773 782 589</span>
        </a>
      </div>
    </div>
  </div>
</template>

<style scoped lang="postcss">
.field {
  @apply bg-transparent border-0 focus:outline-none border-b pl-40 w-full text-[19px] h-[48px] leading-[48px];
}

.field-email {
  background: transparent url('/icons/user.svg') left center no-repeat;
}

.field-password {
  background: transparent url('/icons/lock.svg') left center no-repeat;
}
</style>
